/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Listing record in UI v2 (design-reference "Real Estate · Listing record").
 *
 * GET /re/listings/{id} (with the unit's facts and counts) and the listing's
 * viewings (GET /re/viewings?listing_id=). The buyer pipeline is those
 * viewings: who came, how interested, what they said, what happens next.
 * The activity panel is built from the same records; nothing here is made up.
 *
 * Edits go through the classic listing form (PUT /re/listings/{id}); a stage
 * is changed by clicking it, as on the classic page's status field.
 */

import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { CalendarPlus, House, Pencil } from "lucide-react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "components/v2/PageHeader";
import StagePath from "components/v2/StagePath";
import KeyFieldStrip from "components/v2/KeyFieldStrip";
import UserLink from "components/v2/UserLink";
import { EntityFormDialog } from "components/EntityFramework";
import { usePermission } from "auth/usePermission";
import { v2 as t } from "styles/tokens/v2";
import { errorMessage, formatDate, formatDateTime, formatNumber, humanize, useReApi, useReGet, useReList } from "../screens/api";
import { getReEntityConfig } from "../reEntityConfig";
import { localiseConfig } from "../reEntityVariant";
import useReVariant from "../useReVariant";
import { isAustralia } from "../variant";
import { Panel, Pill, RE_ACCENT, coverUrl, listingBadge, listingPrice } from "./parts";
import { listingActivity } from "./logic";

/** Listing statuses as a stage path; Withdrawn and Expired end it early. */
export const LISTING_STAGES = ["DRAFT", "ACTIVE", "UNDER_OFFER", "CLOSED"];
const STAGE_LABEL = { DRAFT: "Off market", ACTIVE: "Listed", UNDER_OFFER: "Under offer", CLOSED: "Sold / leased" };

const INTEREST = { HOT: "danger", WARM: "warning", COLD: "info" };

function Facts({ items }) {
  return (
    <Box component="dl" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, columnGap: 3, rowGap: 2, m: 0 }}>
      {items.filter((i) => i.value !== null && i.value !== undefined && i.value !== "").map((i) => (
        <Box key={i.label} sx={{ borderBottom: `1px solid ${t.border}`, pb: 1.25 }}>
          <Typography component="dt" sx={{ fontSize: 13, color: t.text3 }}>{i.label}</Typography>
          <Typography component="dd" sx={{ m: 0, fontSize: 15, color: t.text, fontWeight: 500 }}>{i.value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function BuyerPipeline({ viewings }) {
  if (!viewings.length) {
    return <Typography sx={{ fontSize: 14, color: t.text3 }}>No buyers yet. Book an inspection to start the pipeline.</Typography>;
  }
  return (
    <Box sx={{ border: `1px solid ${t.border}`, borderRadius: "8px" }}>
      {viewings.map((v, i) => (
        <Box key={v.id} sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, py: 1.5, borderTop: i ? `1px solid ${t.border}` : "none" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>{v.contact_name || v.lead_name || "Unnamed buyer"}</Typography>
            <Typography sx={{ fontSize: 13, color: t.text3 }} noWrap>
              {[v.feedback, v.next_step].filter(Boolean).join(" · ") || `${humanize(v.status)} · ${formatDateTime(v.scheduled_at)}`}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13.5, color: t.text2, display: { xs: "none", sm: "block" } }}>{humanize(v.status)}</Typography>
          {v.interest_level && <Pill tone={INTEREST[v.interest_level] || "neutral"}>{humanize(v.interest_level)}</Pill>}
        </Box>
      ))}
    </Box>
  );
}

export default function ListingRecordV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const pack = useReVariant();
  const { api } = useReApi();
  const { canEdit } = usePermission("re_listings");
  const { data: l, loading, error, reload, setData } = useReGet(`/re/listings/${id}`);
  const viewings = useReList("/re/viewings", { listing_id: id }, { pageSize: 100 });
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [stageError, setStageError] = useState(null);
  const config = useMemo(() => localiseConfig(getReEntityConfig("re-listings"), pack), [pack]);
  const au = isAustralia(pack);
  const viewingWord = au ? "Inspection" : "Viewing";

  if (loading && !l) {
    return <DashboardLayout><DashboardNavbar /><Box sx={{ pt: 3 }}><Skeleton variant="rounded" height={260} /></Box></DashboardLayout>;
  }
  if (!l) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ pt: 3 }}>
          <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>{errorMessage(error, "This listing could not be loaded.")}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  const badge = listingBadge(l);
  const ended = ["WITHDRAWN", "EXPIRED"].includes(l.status);
  const stageIndex = Math.max(0, LISTING_STAGES.indexOf(l.status));
  const agent = l.assigned_to_name || l.broker_agent_name;
  // Broker agents are not users: only an assigned user's name links.
  const agentUserId = l.assigned_to_name ? l.assigned_to : null;
  const method = l.sale_method
    ? `${humanize(l.sale_method)}${l.sale_method === "AUCTION" && l.auction_at ? `, ${formatDateTime(l.auction_at)}` : ""}`
    : humanize(l.purpose === "RENT" ? (au ? "Lease" : "Rent") : "Sale");

  const changeStage = async (index) => {
    const next = LISTING_STAGES[index];
    if (!canEdit || !next || next === l.status) return;
    setStageError(null);
    try {
      const res = await api.put(`/re/listings/${l.id}`, { status: next });
      setData((cur) => ({ ...cur, ...res.data }));
    } catch (e) {
      setStageError(errorMessage(e, "The stage could not be changed."));
    }
  };

  const activity = listingActivity(l, viewings.items);
  const photos = (l.media || []).map((m) => (typeof m === "string" ? m : m?.url)).filter(Boolean);
  const cover = coverUrl(l);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6 }}>
        <PageHeader
          title={l.title}
          summary={(l.listed_date || agent) ? (
            <>
              {[l.listed_date && `Listed ${formatDate(l.listed_date)}`, agent && "by "].filter(Boolean).join(" ")}
              {agent && <UserLink userId={agentUserId} name={agent} tone="link" />}
            </>
          ) : null}
          actions={(
            <>
              {canEdit && (
                <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={() => setEditing(true)}>Edit</Button>
              )}
              <Button variant="contained" startIcon={<CalendarPlus size={16} />} onClick={() => navigate("/real-estate/viewings")} sx={{ color: "#FFFFFF !important" }}>
                {`Book ${viewingWord.toLowerCase()}`}
              </Button>
            </>
          )}
        />

        <Panel sx={{ mb: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 56, height: 56, borderRadius: "10px", flexShrink: 0, bgcolor: "#FBF1E7", color: RE_ACCENT.ink,
                display: "flex", alignItems: "center", justifyContent: "center",
                backgroundImage: cover ? `url(${cover})` : "none", backgroundSize: "cover", backgroundPosition: "center",
              }}
            >
              {!cover && <House size={24} aria-hidden />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: t.text3 }}>
                {`Listing · ${l.purpose === "RENT" ? (au ? "For lease" : "For rent") : "For sale"}${l.sale_method === "AUCTION" ? " by auction" : ""}`}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 0.25 }}>
                <Typography component="h2" sx={{ fontSize: 22, fontWeight: 600, color: t.text }}>
                  {[l.title, l.address].filter(Boolean).join(", ")}
                </Typography>
                <Pill tone={badge.tone}>{badge.label}</Pill>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <StagePath
              steps={LISTING_STAGES.map((s) => STAGE_LABEL[s])}
              activeStep={ended ? -1 : stageIndex}
              onStepClick={canEdit && !ended ? changeStage : undefined}
              finalState={ended ? humanize(l.status) : null}
            />
          </Box>
          {ended && <Alert severity="warning" sx={{ mt: 1.5 }}>{`This listing is ${humanize(l.status).toLowerCase()}.`}</Alert>}
          {stageError && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setStageError(null)}>{stageError}</Alert>}

          <KeyFieldStrip
            items={[
              { label: l.purpose === "RENT" ? "Rent" : "Price guide", value: listingPrice(l) },
              { label: "Method", value: method },
              { label: "Days on market", value: l.days_on_market != null ? formatNumber(l.days_on_market) : "—" },
              { label: "Enquiries", value: formatNumber(l.enquiry_count || 0) },
              { label: `${viewingWord}s`, value: formatNumber(l.viewing_count || 0) },
            ]}
          />
        </Panel>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(280px, 1fr)" }, gap: 2.5 }}>
          <Panel sx={{ p: 0 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2, borderBottom: `1px solid ${t.border}` }}>
              <Tab value="overview" label="Overview" />
              <Tab value="viewings" label={`${viewingWord}s (${formatNumber(viewings.total || 0)})`} />
              <Tab value="details" label="Details" />
              <Tab value="photos" label={`Photos (${photos.length})`} />
            </Tabs>
            <Box sx={{ p: 2.5 }}>
              {tab === "overview" && (
                <>
                  <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, color: t.text, mb: 1.5 }}>Buyer pipeline</Typography>
                  <BuyerPipeline viewings={viewings.items.filter((v) => v.status !== "CANCELLED")} />
                  <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, color: t.text, mt: 3, mb: 1.5 }}>Property</Typography>
                  <Facts
                    items={[
                      { label: "Type", value: humanize(l.category) },
                      { label: "Bedrooms", value: l.bedrooms === 0 ? "Studio" : l.bedrooms },
                      { label: "Bathrooms", value: l.bathrooms },
                      { label: "Car spaces", value: l.parking_spaces },
                      { label: "Land", value: l.land_size_sqm ? `${formatNumber(l.land_size_sqm)} m²` : null },
                      { label: "Built area", value: l.bua_sqft ? `${formatNumber(l.bua_sqft)} sq ft` : null },
                      { label: l.purpose === "RENT" ? "Landlord" : (au ? "Vendor" : "Owner"), value: l.landlord_contact_name },
                      { label: "Listing agent", value: agent ? <UserLink userId={agentUserId} name={agent} tone="link" /> : agent },
                      { label: "Portals", value: (l.portals || []).map((p) => (pack.listing_portals || []).find((x) => x.code === p)?.label || humanize(p)).join(", ") },
                    ]}
                  />
                  {l.description && (
                    <Typography sx={{ mt: 2.5, fontSize: 14.5, color: t.text2, whiteSpace: "pre-line" }}>{l.description}</Typography>
                  )}
                </>
              )}
              {tab === "viewings" && <BuyerPipeline viewings={viewings.items} />}
              {tab === "details" && (
                <Facts
                  items={[
                    { label: "Reference", value: l.reference },
                    { label: "Project", value: l.project_name },
                    { label: "Unit", value: l.unit_number },
                    { label: "Listed", value: l.listed_date ? formatDate(l.listed_date) : null },
                    { label: "Agency agreement", value: humanize(l.agency_agreement_type) },
                    { label: "Agent licence", value: l.agent_licence_no },
                    { label: "Trakheesi permit", value: au ? null : l.trakheesi_permit_no },
                    { label: "Form A", value: au ? null : l.form_a_no },
                    { label: "Form A expiry", value: !au && l.form_a_expiry ? formatDate(l.form_a_expiry) : null },
                  ]}
                />
              )}
              {tab === "photos" && (
                photos.length ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.5 }}>
                    {photos.map((src) => (
                      <Box key={src} component="img" src={src} alt="" sx={{ width: "100%", height: 130, objectFit: "cover", borderRadius: "8px" }} />
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 14, color: t.text3 }}>No photos on this listing. Unit photos are on the unit's page.</Typography>
                )
              )}
            </Box>
          </Panel>

          <Panel title="Activity">
            {activity.length ? (
              <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 2 }}>
                {activity.slice(0, 12).map((e) => {
                  const Icon = e.icon;
                  return (
                    <Box component="li" key={e.key} sx={{ display: "flex", gap: 1.5 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: t.surface2, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: t.text2, flexShrink: 0 }}>
                        <Icon size={15} aria-hidden />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>{e.title}</Typography>
                          <Typography sx={{ fontSize: 12.5, color: t.text3, whiteSpace: "nowrap" }}>{formatDate(e.at)}</Typography>
                        </Box>
                        {e.detail && <Typography sx={{ fontSize: 13, color: t.text3 }}>{e.detail}</Typography>}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 14, color: t.text3 }}>Nothing yet.</Typography>
            )}
          </Panel>
        </Box>
      </Box>

      {canEdit && (
        <EntityFormDialog
          open={editing}
          onClose={() => setEditing(false)}
          title="Edit listing"
          apiPath="/re/listings"
          fields={config.editFields || config.createForm.fields}
          record={l}
          onCreated={(row) => {
            setEditing(false);
            if (row) setData((cur) => ({ ...cur, ...row }));
            else reload();
          }}
        />
      )}
    </DashboardLayout>
  );
}
